"use client";

// Paso previo al wizard de reserva: si el visitante ya es paciente con un programa de
// tratamiento activo, identificarlo por email desbloquea sus servicios de programa.
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";

const ppSurface = css({
  bg: "var(--pp-card-bg, #ffffff)",
  color: "var(--pp-text, #0f172a)",
  borderColor: "var(--pp-border, #e2e8f0)",
});

const fieldStyle = css({
  width: "100%",
  px: "3",
  py: "2.5",
  rounded: "md",
  borderWidth: "1px",
  borderColor: "var(--pp-border, #e2e8f0)",
  bg: "#ffffff",
  color: "#0f172a",
  fontSize: "sm",
  _placeholder: { color: "#64748b" },
  _focus: { outline: "none", borderColor: "token(colors.brand.500)", boxShadow: "0 0 0 1px token(colors.brand.500)" },
});

export function PatientProgramGate({
  primaryColor,
  busy,
  onContinue,
  onSkip,
}: {
  primaryColor: string;
  busy: boolean;
  onContinue: (email: string) => void;
  onSkip: () => void;
}) {
  const [email, setEmail] = useState("");

  function submit() {
    const trimmed = email.trim();
    if (!trimmed) {
      onSkip();
      return;
    }
    onContinue(trimmed);
  }

  return (
    <Card className={ppSurface}>
      <div className={css({ display: "flex", flexDir: "column", gap: "4", p: { base: "1", sm: "2" } })}>
        <div>
          <h2
            className={css({ fontSize: { base: "lg", lg: "xl" }, fontWeight: "bold", letterSpacing: "-0.02em" })}
            style={{ color: "var(--pp-text, inherit)" }}
          >
            ¿Ya sos paciente?
          </h2>
          <p className={css({ fontSize: "sm", mt: "1.5", lineHeight: "1.5" })} style={{ color: "var(--pp-muted, inherit)" }}>
            Ingresá tu email para ver tus consultas disponibles si tenés un programa de tratamiento activo.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className={css({ display: "flex", flexDir: { base: "column", sm: "row" }, gap: "2" })}
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            className={cx(fieldStyle, css({ flex: "1" }))}
            disabled={busy}
          />
          <button
            type="submit"
            disabled={busy}
            className={css({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "1.5",
              px: "5",
              py: "2.5",
              rounded: "md",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              border: "none",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "opacity 0.15s, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              _hover: { opacity: 0.92 },
              _active: { transform: "scale(0.97)" },
              _disabled: { opacity: 0.6, cursor: "not-allowed" },
            })}
            style={{ backgroundColor: primaryColor }}
          >
            {busy ? "Buscando…" : "Continuar"}
            {!busy && <ArrowRight size={16} aria-hidden />}
          </button>
        </form>

        <button
          type="button"
          onClick={onSkip}
          disabled={busy}
          className={css({
            alignSelf: "flex-start",
            fontSize: "sm",
            fontWeight: "medium",
            bg: "transparent",
            border: "none",
            cursor: "pointer",
            p: "0",
            textDecoration: "underline",
            _hover: { opacity: 0.75 },
            _disabled: { opacity: 0.5, cursor: "not-allowed" },
          })}
          style={{ color: "var(--pp-muted, #64748b)" }}
        >
          Reservar sin programa
        </button>
      </div>
    </Card>
  );
}
